// Manual-readings family (SEN-395): the Meters page "Manual entry" grid on NomOS `readings/manual`.
//
//   getManualReadingGrid   <- meters with reading_entry = manual (records) + kWh by meter x month (one time-bucket question)
//                             + POST /readings/manual/cells (who/when/provenance of the newest entry per cell)
//   writeManualCell        <- POST /readings/manual   (one cell; value null clears)
//   applyManualBulkPaste   <- POST /readings/manual   (cells, reason bulk_paste; null positions are skipped)
//   getCellHistory         <- GET  /readings/manual/history
//   undoCell               <- POST /readings/manual/undo
// Writes answer with the meter's updated GridRow (re-read through the same grid builder), as Sentinel does.
// The engine owns the rule: the typed month total wins, metered hours are kept and the remainder is spread; a total
// below the metered hours is 409 `readings:below_metered`, mapped here to a user message (da / en).
// Nothing is summed client-side: cell numbers are the engine's `kwh` by meter and month.
// Differences from Sentinel, named: meter_point_id is the Sentinel meter id when the meter has one, else the NomOS record
// id; `adapter_ids` is [] (NomOS has no per-meter data-source rows); volume-only (m3) meters are listed read-only with
// empty cells (their quantity is not a manual-readings quantity); months are bucketed on the tenant calendar.
import type { AxiosError } from 'axios';
import type { BulkPasteRequest, BulkPasteResponse, CellHistoryEntry, CellSource, CellWriteRequest, CellWriteResponse, GridCell,
  GridResponse, GridRow } from '@/types/manualEntry';
import { nomosFetch, queryRecords, refs, runQuery, scalar, type NomosEnvelope, type NomosFilter } from '../client';
import { resolveRecordIds } from '../ids';
import type { AnyFn, NomosRegistry } from '../registry';
import { localMidnight, tenantZone } from '../tz';
import { tenantFiscalStartMonth } from './reference';
import { fiscalYearMonths, fiscalYearRangeLabel } from '@/lib/plan-year';

type Row = Record<string, unknown>;
const MONTHS = 12;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ---- user messages (the app has no i18n library: Danish when the browser says so, else English) ----

export type Lang = 'da' | 'en';
export function userLang(): Lang {
  try {
    const l = typeof navigator !== 'undefined' ? navigator.language : '';
    return /^da\b/i.test(l ?? '') ? 'da' : 'en';
  } catch { return 'en'; }
}
const kwh = (n: number, lang: Lang) => `${new Intl.NumberFormat(lang === 'da' ? 'da-DK' : 'en-GB', { maximumFractionDigits: 2 }).format(n)} kWh`;

/** The message for `readings:below_metered` (typed month total below the hours already metered in that month). */
export function belowMeteredMessage(typed: number | null, metered: number | null, lang: Lang = userLang()): string {
  const known = typeof typed === 'number' && typeof metered === 'number';
  if (lang === 'da') {
    return known
      ? `Den indtastede månedssum (${kwh(typed, lang)}) er lavere end det, der allerede er målt i måneden (${kwh(metered, lang)}). Indtast mindst ${kwh(metered, lang)}.`
      : 'Den indtastede månedssum er lavere end det, der allerede er målt i måneden.';
  }
  return known
    ? `The typed month total (${kwh(typed, lang)}) is lower than what is already metered in that month (${kwh(metered, lang)}). Enter at least ${kwh(metered, lang)}.`
    : 'The typed month total is lower than what is already metered in that month.';
}

/** Re-throw a NomOS refusal with the user's message in `detail` / `message` (the screens show `e.message`). */
function mapRefusal(e: unknown): never {
  const err = e as AxiosError<{ detail?: string; error?: { code?: string; details?: { typed?: number; metered?: number } } }>;
  const body = err.response?.data;
  if (err.response?.status === 409 && body?.error?.code === 'readings:below_metered') {
    const msg = belowMeteredMessage(body.error.details?.typed ?? null, body.error.details?.metered ?? null);
    err.message = msg;
    if (err.response?.data) err.response.data.detail = msg;
  }
  throw err;
}

// ---- calendar ----

interface FiscalYear { fiscalYear: number; months: string[]; start: string; end: string; label: string; monthLabels: string[] }

/**
 * Plan year `fy` as 12 calendar months `YYYY-MM`. `fy` is the year the fiscal year ENDS (lib/plan-year.ts, the
 * convention of the period selector, Targets and NomOS plan_year): FY2027 of a September tenant is Sep 2026 – Aug 2027.
 * Until 2026-10-20 this read `fy` as the START year while the page sent the end year, so the grid opened on the
 * next, future fiscal year and a reading typed in "Sep" was stored for September of the following year.
 */
async function fiscalYearOf(fy: number): Promise<FiscalYear> {
  const [startMonth, zone] = await Promise.all([tenantFiscalStartMonth(), tenantZone()]);
  const months = fiscalYearMonths(fy, startMonth);
  const next = (ym: string) => { const [y, m] = ym.split('-').map(Number); return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`; };
  const first = months[0];
  const last = months[MONTHS - 1];
  return { fiscalYear: fy, months, start: localMidnight(`${first}-01`, zone), end: localMidnight(`${next(last)}-01`, zone),
    label: fiscalYearRangeLabel(fy, startMonth), monthLabels: months.map((ym) => MONTH_NAMES[Number(ym.slice(5, 7)) - 1]) };
}

/** Calendar month `YYYY-MM` of 0-based fiscal month index `i`. */
async function monthOf(fy: number, i: number): Promise<string> {
  if (!Number.isInteger(i) || i < 0 || i >= MONTHS) throw badRequest(`fiscal month index ${i} is outside 0-${MONTHS - 1}`);
  return (await fiscalYearOf(fy)).months[i];
}

function badRequest(detail: string): AxiosError {
  const e = new Error(detail) as AxiosError;
  e.name = 'AxiosError';
  e.isAxiosError = true;
  e.response = { status: 400, statusText: '', data: { detail }, headers: {}, config: {} as never };
  return e;
}

// ---- ids ----

/** The NomOS meter record id for a grid `meter_point_id` (a Sentinel meter id, or a record id of a NomOS-made meter). */
async function meterRecord(id: string): Promise<string> {
  const hit = (await resolveRecordIds('meter', [id])).get(id);
  if (hit) return hit;
  if (UUID_RE.test(id)) return id;
  throw badRequest(`Meter ${id} not found`);
}

// ---- grid ----

const num = (x: unknown): number | null => { const s = scalar(x); return typeof s === 'number' && Number.isFinite(s) ? s : null; };
const str = (x: unknown): string | null => { const s = scalar(x); return typeof s === 'string' && s !== '' ? s : null; };

async function allMeters(filter: NomosFilter): Promise<Array<{ id: string; v: Row }>> {
  const out: Array<{ id: string; v: Row }> = [];
  for (let offset = 0; ; offset += 1000) {
    const res = await queryRecords('meter', { fields: ['name', 'site', 'energy_source', 'is_active', 'sentinel_meter_id'], filter, limit: 1000, cursor: offset });
    out.push(...res.rows.map((r) => ({ id: r.record_id, v: r.values })));
    if (res.rows.length === 0 || out.length >= res.total) break;
  }
  return out;
}

/** Cells (`readings/manual/cells`) of the given meters (undefined = every meter the member sees) over the fiscal year. */
async function provenanceCells(fy: FiscalYear, meterIds?: string[]) {
  const res = await nomosFetch<NomosEnvelope<Array<{ meter_id: string; month: string; provenance: string; last_written_at: string; last_written_by: string | null }>>>(
    'POST', '/readings/manual/cells', { quantity: 'kwh', from: fy.months[0], to: fy.months[MONTHS - 1], ...(meterIds ? { meter_ids: meterIds } : {}) });
  return res.data ?? [];
}

/** kWh by meter x month: one engine time-bucket question per page. */
async function monthlyKwh(fy: FiscalYear, filter: NomosFilter): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  let cursor: string | undefined;
  for (let guard = 0; guard < 100; guard++) {
    const res = await runQuery<{ rows: Row[]; meta: Row }>({ op: 'reduce',
      scope: { entity_def: 'Meter', quantity: 'kwh', period_start: fy.start, period_end: fy.end, filter },
      reducer: { kind: 'time-bucket', agg: 'sum', granularity: 'month', dimensions: ['site_id'] }, page: { size: 10_000, ...(cursor ? { cursor } : {}) } });
    for (const r of res.data?.rows ?? []) {
      const v = num(r.value);
      if (v !== null) out.set(`${String(r.site_id)}|${String(r.bucket_date).slice(0, 7)}`, v);
    }
    cursor = ((res.data?.meta?.page as Row | undefined)?.cursor as string | null | undefined) ?? undefined;
    if (!cursor) break;
  }
  return out;
}

async function siteInfo(siteIds: string[]): Promise<Map<string, { name: string; country: string }>> {
  const out = new Map<string, { name: string; country: string }>();
  if (!siteIds.length) return out;
  const [sites, countries] = await Promise.all([
    queryRecords('site', { fields: ['name', 'country_ref'], filter: { attribute: 'record_id', op: 'in', value: siteIds }, limit: siteIds.length }),
    queryRecords('country', { fields: ['code'], limit: 1000 }),
  ]);
  const code = new Map(countries.rows.map((c) => [c.record_id, str(c.values.code) ?? 'XX']));
  for (const s of sites.rows) out.set(s.record_id, { name: str(s.values.name) ?? s.kenning, country: code.get(refs(s.values.country_ref)[0]?.id ?? '') ?? 'XX' });
  return out;
}

async function sourceInfo(): Promise<Map<string, { code: string; unit: string; volumeOnly: boolean }>> {
  const res = await queryRecords('energy_source', { fields: ['code', 'unit', 'kwh_conversion_factor'], limit: 1000 });
  return new Map(res.rows.map((r) => {
    const unit = str(r.values.unit) ?? 'kWh';
    return [r.record_id, { code: str(r.values.code) ?? 'other', unit, volumeOnly: unit === 'm3' && num(r.values.kwh_conversion_factor) === null }];
  }));
}

const cellSource = (provenance: string | undefined, value: number | null): CellSource => {
  if (value === null || !provenance) return 'none';
  if (provenance === 'reported_ra' || provenance === 'forecast') return provenance;
  return 'typed';   // typed, and the migrated value of a cell
};

/** GridRows for the meters matching `meterFilter` (the grid: every manual meter; a write: the one meter). */
async function buildRows(fy: FiscalYear, meterFilter: NomosFilter, meterIds?: string[]): Promise<GridRow[]> {
  const meters = (await allMeters(meterFilter)).filter((m) => scalar(m.v.is_active) !== false);
  if (!meters.length) return [];
  const siteIds = [...new Set(meters.map((m) => refs(m.v.site)[0]?.id).filter((x): x is string => !!x))];
  const [kwhByCell, cells, sites, sources] = await Promise.all([
    monthlyKwh(fy, meterFilter), provenanceCells(fy, meterIds), siteInfo(siteIds), sourceInfo()]);
  const prov = new Map(cells.map((c) => [`${c.meter_id}|${c.month}`, c]));
  const rows = meters.map((m): GridRow => {
    const src = sources.get(refs(m.v.energy_source)[0]?.id ?? '');
    const site = sites.get(refs(m.v.site)[0]?.id ?? '');
    const volumeOnly = src?.volumeOnly ?? false;
    const gridCells: GridCell[] = fy.months.map((month, i) => {
      const raw = volumeOnly ? null : kwhByCell.get(`${m.id}|${month}`) ?? null;
      const value = raw === null ? null : Math.round(raw * 1e4) / 1e4;   // NomOS stores 4 dp; this drops float-sum noise (1000.0000000000183)
      const p = prov.get(`${m.id}|${month}`);
      return { month_index: i, value_kwh: value, last_written_at: p?.last_written_at ?? null, last_written_by: p?.last_written_by ?? null,
        has_automated_data: false, source: cellSource(p?.provenance, value) };
    });
    return { meter_point_id: str(m.v.sentinel_meter_id) ?? m.id, meter_label: str(m.v.name) ?? 'Meter', site_label: site?.name ?? 'Unassigned',
      country_code: site?.country ?? 'XX', energy_type: src?.code ?? 'other', unit: src?.unit ?? 'kWh', editable: !volumeOnly, adapter_ids: [], cells: gridCells };
  });
  return rows.sort((a, b) => a.site_label.localeCompare(b.site_label) || a.meter_label.localeCompare(b.meter_label));
}

async function getManualReadingGrid(fiscalYear: number): Promise<GridResponse> {
  const fy = await fiscalYearOf(fiscalYear);
  const rows = await buildRows(fy, { attribute: 'reading_entry', op: 'eq', value: 'manual' });
  return { fiscal_year: fiscalYear, fiscal_year_label: fy.label, months: fy.months, period_start_utc: fy.start, period_end_utc: fy.end, month_labels: fy.monthLabels, rows };
}

/** The updated row of one meter after a write. */
async function rowOf(fy: FiscalYear, record: string): Promise<GridRow> {
  const [row] = await buildRows(fy, { attribute: 'record_id', op: 'in', value: [record] }, [record]);
  if (!row) throw badRequest('Meter not found or not active');
  return row;
}

// ---- writes ----

interface WriteResult { write_id: string; cells: Array<{ meter_id: string; month: string; before: number | null; after: number | null; entry_id: string }> }

async function post(path: string, body: Row): Promise<WriteResult> {
  try {
    return (await nomosFetch<NomosEnvelope<WriteResult>>('POST', path, body)).data;
  } catch (e) { return mapRefusal(e); }
}

const opId = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now().toString(16).padStart(8, '0')}-0000-4000-8000-${Math.random().toString(16).slice(2).padEnd(12, '0').slice(0, 12)}`);

async function writeManualCell(req: CellWriteRequest): Promise<CellWriteResponse> {
  const [record, month, fy] = await Promise.all([meterRecord(req.meter_point_id), monthOf(req.fiscal_year, req.fiscal_month_index), fiscalYearOf(req.fiscal_year)]);
  const res = await post('/readings/manual', { quantity: 'kwh', cells: [{ meter_id: record, month, value: req.value_kwh }], client_op_id: opId() });
  return { log_id: res.cells[0].entry_id, row: await rowOf(fy, record) };
}

async function applyManualBulkPaste(req: BulkPasteRequest): Promise<BulkPasteResponse> {
  const fy = await fiscalYearOf(req.fiscal_year);
  const record = await meterRecord(req.meter_point_id);
  if (req.starting_month_index < 0 || req.starting_month_index + req.values_kwh.length > MONTHS) {
    throw badRequest(`Paste exceeds FY: starting_month_index=${req.starting_month_index}, values=${req.values_kwh.length}`);
  }
  const cells = req.values_kwh.flatMap((v, i) => (v === null ? [] : [{ meter_id: record, month: fy.months[req.starting_month_index + i], value: v }]));
  if (!cells.length) return { correlation_id: opId(), cells_written: 0, row: await rowOf(fy, record) };
  const res = await post('/readings/manual', { quantity: 'kwh', cells, reason: 'bulk_paste', client_op_id: opId() });
  return { correlation_id: res.write_id, cells_written: res.cells.length, row: await rowOf(fy, record) };
}

async function undoCell(meterPointId: string, fiscalYear: number, monthIndex: number): Promise<CellWriteResponse> {
  const [record, month, fy] = await Promise.all([meterRecord(meterPointId), monthOf(fiscalYear, monthIndex), fiscalYearOf(fiscalYear)]);
  const res = await post('/readings/manual/undo', { meter_id: record, quantity: 'kwh', month });
  return { log_id: res.cells[0].entry_id, row: await rowOf(fy, record) };
}

async function getCellHistory(meterPointId: string, fiscalYear: number, monthIndex: number): Promise<CellHistoryEntry[]> {
  const [record, month] = await Promise.all([meterRecord(meterPointId), monthOf(fiscalYear, monthIndex)]);
  const res = await nomosFetch<NomosEnvelope<Array<{ entry_id: string; before: number | null; after: number | null; applied_at: string;
    actor: { display: string | null }; source: string }>>>('GET', '/readings/manual/history', undefined, { query: { meter_id: record, month, quantity: 'kwh', limit: 20 } });
  return (res.data ?? []).map((e) => ({ log_id: e.entry_id, before_value_kwh: e.before, after_value_kwh: e.after ?? 0, written_at: e.applied_at,
    written_by: e.actor?.display ?? null, is_undo: e.source === 'undo' }));
}

export const manualReadingsImpl: NomosRegistry = {
  getManualReadingGrid: getManualReadingGrid as unknown as AnyFn,
  writeManualCell: writeManualCell as unknown as AnyFn,
  getCellHistory: getCellHistory as unknown as AnyFn,
  undoCell: undoCell as unknown as AnyFn,
  applyManualBulkPaste: applyManualBulkPaste as unknown as AnyFn,
};
